import React from 'react';
import { FileText, Scale, AlertCircle, CheckCircle } from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const TermsOfService: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [contentRef, contentVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#faf8ed]">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 md:pt-52 pb-20 px-4 bg-mesh-premium relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 bg-white/20 backdrop-blur-3xl"></div>
        <div className={`container-premium text-center transition-all duration-700 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
          <div className="flex justify-center mb-8">
            <div className="bg-[#0c513f] p-6 rounded-[1.5rem] shadow-xl shadow-[#0c513f]/20">
              <Scale className="text-white" size={48} />
            </div>
          </div>
          <h1 className="heading-huge text-[#0c513f] mb-6 tracking-tighter-premium">
            Terms of Service
          </h1>
          <p className="text-2xl text-secondary-600 max-w-3xl mx-auto leading-tight font-medium">
            Please read these terms carefully before using Rita's services.
          </p>
          <p className="text-sm font-bold text-[#0c513f]/60 mt-6 uppercase tracking-widest">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section ref={contentRef} className="py-20 px-4">
        <div className={`container-premium max-w-5xl bg-white rounded-[3rem] p-6 md:p-16 shadow-2xl shadow-[#0c513f]/5 border border-[#0c513f]/5 relative ${contentVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
          <div className="space-y-16 text-secondary-700">
            {/* Agreement */}
            <div>
              <h2 className="text-4xl font-black text-[#0c513f] mb-6 flex items-center gap-4">
                <div className="bg-[#faf8ed] p-2 rounded-xl">
                  <FileText className="text-[#0c513f]" size={32} />
                </div>
                1. Agreement to Terms
              </h2>
              <p className="text-xl leading-relaxed font-medium">
                By accessing or using Rita's QR payment and discovery platform ("Service"), you agree to be bound by these Terms
                of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
              </p>
              <p className="text-xl leading-relaxed mt-4 font-medium">
                These Terms apply to all users of the Service, including customers, businesses, merchants, and any other users
                who access or use the Service.
              </p>
            </div>

            {/* Description of Service */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                2. Description of Service
              </h2>
              <p className="text-xl leading-relaxed mb-6 font-medium">
                Rita provides a universal platform that enables:
              </p>
              <ul className="grid md:grid-cols-2 gap-4 text-lg leading-relaxed font-medium">
                {[
                  'QR code-based payment processing and digital wallet services',
                  'Business discovery and location-based services',
                  'Navigation and mapping services to business locations',
                  'Business management tools and analytics for merchants',
                  'Transaction processing and settlement services'
                ].map((item, i) => (
                  <li key={i} className="flex gap-4 bg-[#faf8ed] p-4 rounded-xl border border-[#0c513f]/5 hover:border-[#0c513f]/20 transition-colors">
                    <span className="w-2 h-2 rounded-full bg-[#0c513f] mt-2.5 flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* User Accounts */}
            <div>
              <h2 className="text-4xl font-black text-[#0c513f] mb-6 flex items-center gap-4">
                <div className="bg-[#faf8ed] p-2 rounded-xl">
                  <CheckCircle className="text-[#0c513f]" size={32} />
                </div>
                3. User Accounts
              </h2>

              <div className="bg-[#faf8ed]/50 p-8 rounded-[2rem] border border-[#0c513f]/5 mb-8">
                <h3 className="text-2xl font-bold text-[#0c513f] mb-4">
                  3.1 Account Registration
                </h3>
                <p className="text-xl leading-relaxed font-medium mb-4">
                  To use certain features of the Service, you must register for an account. You agree to:
                </p>
                <ul className="space-y-2 text-lg leading-relaxed font-medium pl-4 border-l-2 border-[#0c513f]/20">
                  {[
                    'Provide accurate, current, and complete information during registration',
                    'Maintain and update your account information to keep it accurate',
                    'Maintain the security of your account credentials',
                    'Accept responsibility for all activities under your account',
                    'Notify us immediately of any unauthorized use of your account'
                  ].map((item, i) => (
                    <li key={i} className="pl-4">{item}</li>
                  ))}
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-[#0c513f] mb-4">
                3.2 Account Eligibility
              </h3>
              <p className="text-xl leading-relaxed font-medium">
                You must be at least 18 years old to create an account. By creating an account, you represent and warrant that
                you meet this age requirement and have the legal capacity to enter into these Terms.
              </p>
            </div>

            {/* Acceptable Use */}
            <div>
              <h2 className="text-4xl font-black text-[#0c513f] mb-6 flex items-center gap-4">
                <div className="bg-[#faf8ed] p-2 rounded-xl">
                  <AlertCircle className="text-[#0c513f]" size={32} />
                </div>
                4. Acceptable Use
              </h2>
              <p className="text-xl leading-relaxed mb-6 font-medium">
                You agree not to use the Service to:
              </p>
              <ul className="space-y-3 text-lg leading-relaxed font-medium">
                {[
                  'Violate any applicable laws, regulations, or third-party rights',
                  'Engage in fraudulent, deceptive, or illegal activities',
                  'Transmit viruses, malware, or other harmful code',
                  'Attempt to gain unauthorized access to the Service',
                  'Interfere with or disrupt the Service or servers',
                  'Use automated systems to access the Service without authorization',
                  'Impersonate any person or entity or misrepresent your affiliation',
                  'Collect or harvest information about other users',
                  'Use the Service for any commercial purpose without our express written consent'
                ].map((item, i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <span className="w-2 h-2 rounded-full bg-red-500 mt-2.5 flex-shrink-0 shadow-sm"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Payments and Transactions */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                5. Payments and Transactions
              </h2>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-[#0c513f] mb-3">5.1 Payment Processing</h3>
                  <p className="text-lg leading-relaxed font-medium mb-4">Rita facilitates payment processing between users and businesses. You agree to provide accurate information and authorize us to process payments.</p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#0c513f] mb-3">5.2 Transaction Fees</h3>
                  <p className="text-lg leading-relaxed font-medium mb-4">We may charge transaction fees as disclosed in our pricing terms. Fees are subject to change with prior notice.</p>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-[#0c513f] mb-3 mt-4">
                5.3 Refunds and Disputes
              </h3>
              <p className="text-xl leading-relaxed font-medium">
                Refund policies are determined by individual businesses. Rita facilitates dispute resolution but is not responsible
                for the resolution of disputes between users and businesses. We may hold funds during dispute resolution.
              </p>
            </div>

            {/* Business Users */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                6. Business Users and Merchants
              </h2>
              <div className="bg-[#0c513f] text-white p-6 md:p-8 rounded-[2rem]">
                <p className="text-xl leading-relaxed mb-6 font-medium">
                  If you are a business using Rita's merchant services, you agree to:
                </p>
                <ul className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-lg leading-relaxed font-medium">
                  {[
                    'Complete KYC verification and provide accurate business information',
                    'Comply with all applicable business licenses and regulations',
                    'Honor all transactions processed through the Service',
                    'Maintain accurate business information, hours, and location data',
                    'Respond promptly to customer inquiries and disputes',
                    'Comply with our merchant agreement and payment processing terms'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle size={20} className="text-[#faf8ed] flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Intellectual Property */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                7. Intellectual Property
              </h2>
              <p className="text-xl leading-relaxed font-medium">
                The Service and its original content, features, and functionality are owned by Rita and are protected by
                international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not
                copy, modify, distribute, sell, or lease any part of our Service without our prior written consent.
              </p>
            </div>

            {/* Privacy */}
            <div>
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                8. Privacy
              </h2>
              <p className="text-xl leading-relaxed font-medium">
                Your use of the Service is also governed by our Privacy Policy. Please review our Privacy Policy to understand
                how we collect, use, and protect your information.
              </p>
            </div>

            {/* Disclaimers & Liability */}
            <div className="bg-red-50 p-8 rounded-[2rem] border border-red-100">
              <h2 className="text-3xl font-bold text-red-800 mb-6">
                9. Disclaimers & Limitation of Liability
              </h2>
              <p className="text-lg leading-relaxed mb-4 text-red-900/80 font-medium uppercase tracking-wide">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
              </p>
              <p className="text-lg leading-relaxed text-red-900/80 font-medium uppercase tracking-wide">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, Rita SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, OR PUNITIVE DAMAGES.
              </p>
            </div>

            {/* Remaining Sections */}
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-[#0c513f] mb-4">11. Indemnification</h2>
                <p className="text-lg leading-relaxed font-medium">You agree to indemnify, defend, and hold harmless Rita against any claims arising from your use of the Service.</p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#0c513f] mb-4">12. Termination</h2>
                <p className="text-lg leading-relaxed font-medium">We may terminate or suspend your account immediately, without prior notice, if you breach these Terms.</p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#0c513f] mb-4">13. Changes to Terms</h2>
                <p className="text-lg leading-relaxed font-medium">We reserve the right to modify these Terms at any time. Material changes will be notified.</p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#0c513f] mb-4">14. Governing Law</h2>
                <p className="text-lg leading-relaxed font-medium">These Terms shall be governed by the laws of your jurisdiction.</p>
              </div>
            </div>

            {/* Contact */}
            <div className="border-t border-[#0c513f]/10 pt-12 text-center">
              <h2 className="text-3xl font-bold text-[#0c513f] mb-6">
                15. Contact Information
              </h2>
              <p className="text-xl leading-relaxed font-medium mb-8">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="inline-flex flex-col md:flex-row gap-6 bg-[#faf8ed] p-8 rounded-[2rem] text-left">
                <div className="flex gap-4 items-center">
                  <div className="w-12 h-12 rounded-full bg-[#0c513f] flex items-center justify-center text-white font-bold text-xl">@</div>
                  <div>
                    <p className="text-xs uppercase font-bold text-[#0c513f]/60">Email</p>
                    <a href="mailto:info@rita.ng" className="text-lg font-black text-[#0c513f] hover:underline">legal@rita.ng</a>
                  </div>
                </div>
                <div className="flex gap-4 items-center border-t md:border-t-0 md:border-l border-[#0c513f]/10 pt-4 md:pt-0 md:pl-6">
                  <div className="w-12 h-12 rounded-full bg-[#0c513f] flex items-center justify-center text-white font-bold text-xl">L</div>
                  <div>
                    <p className="text-xs uppercase font-bold text-[#0c513f]/60">Location</p>
                    <p className="text-lg font-black text-[#0c513f]">Nigeria</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;

